import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {TarefaService} from "../../../services/tarefa-service";
import {ToastrService} from "ngx-toastr";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import Swal from "sweetalert2";
import {NovaTarefa} from "../../../models/nova-tarefa";
import {CompromissoService} from "../../../../../core/services/compromisso.service";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-novo-compromisso',
  templateUrl: './novo-compromisso.component.html',
  styleUrl: './novo-compromisso.component.css',
  standalone: false
})
export class NovoCompromissoComponent implements OnInit, OnDestroy {
  @Input() showModal = false;
  @Output() atualizarListaDiario = new EventEmitter();
  @Output() atualizarListaPai = new EventEmitter();
  @Output() fecharModalPai = new EventEmitter<void>();

  formNovoCompromisso: FormGroup;
  novoCompromisso = new NovaTarefa();
  dataInicioMinima = '';
  dataPrevisaoMinima = '';
  private subscription: Subscription;

  constructor(
    private tarefaService: TarefaService,
    private toastr: ToastrService,
    private formBuilder: FormBuilder,
    private compromissoService: CompromissoService
  ) {
    this.construirFormulario();
    this.atualizarLimitesDatas();
    this.subscription = new Subscription();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.compromissoService.abrirCompromisso$.subscribe(data => {
        this.abrirModal();
        this.setarDatas(data);
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  atualizarLista() {
    this.atualizarListaDiario.emit();
    this.atualizarListaPai.emit();
  }

  fecharModal() {
    this.formNovoCompromisso.reset();
    this.showModal = false;
    this.fecharModalPai.emit();
  }

  abrirModal() {
    this.atualizarLimitesDatas();
    this.showModal = true;
  }

  setarDatas(data: string) {
    const hoje = new Date();
    const [year, month, day] = data.split('-').map(num => parseInt(num, 10));
    let dataClicada = new Date(year, month - 1, day);
    dataClicada.setHours(hoje.getHours());
    dataClicada.setMinutes(hoje.getMinutes());
    dataClicada.setSeconds(0, 0);

    const inicioMinimo = new Date(this.dataInicioMinima);
    if (dataClicada < inicioMinimo) {
      dataClicada = inicioMinimo;
    }
    const dataFinal = new Date(dataClicada);
    dataFinal.setHours(dataFinal.getHours() + 1);
    const dataInicialFormatada = this.formatarDataParaInput(dataClicada);
    const dataFinalFormatada = this.formatarDataParaInput(dataFinal);

    this.formNovoCompromisso.patchValue({
      dataInicial: dataInicialFormatada,
      dataPrevisaoFinalizacao: dataFinalFormatada
    });
    this.atualizarPrevisaoMinima(dataInicialFormatada);
  }

  private formatarDataParaInput(data: Date): string {
    const ano = data.getFullYear();
    const mes = (data.getMonth() + 1).toString().padStart(2, '0');
    const dia = data.getDate().toString().padStart(2, '0');
    const hora = data.getHours().toString().padStart(2, '0');
    const minuto = data.getMinutes().toString().padStart(2, '0');

    return `${ano}-${mes}-${dia}T${hora}:${minuto}`;
  }

  onBackgroundClick(event: Event): void {
    this.fecharModal();
  }

  onDataInicialAlterada(): void {
    const dataInicial = this.formNovoCompromisso.controls['dataInicial'].value;
    this.atualizarPrevisaoMinima(dataInicial);

    if (!dataInicial) {
      this.formNovoCompromisso.patchValue({ dataPrevisaoFinalizacao: '' });
      return;
    }

    const previsaoAtual = this.formNovoCompromisso.controls['dataPrevisaoFinalizacao'].value;
    if (!previsaoAtual || new Date(previsaoAtual) <= new Date(dataInicial)) {
      const novaPrevisao = new Date(dataInicial);
      novaPrevisao.setHours(novaPrevisao.getHours() + 1);
      this.formNovoCompromisso.patchValue({
        dataPrevisaoFinalizacao: this.formatarDataParaInput(novaPrevisao)
      });
    }
  }


  salvar() {
    this.preencherModel();
    const inicio = new Date(this.novoCompromisso.dataInicial);
    const previsao = new Date(this.novoCompromisso.dataPrevisaoFinalizacao);

    if (this.formNovoCompromisso.invalid || Number.isNaN(inicio.getTime()) || Number.isNaN(previsao.getTime())) {
      this.toastr.warning('Informe a data inicial e a previsão de término.');
      this.formNovoCompromisso.markAllAsTouched();
      return;
    }

    if (inicio < new Date()) {
      this.toastr.warning('A data inicial não pode estar no passado.');
      return;
    }

    if (previsao <= inicio) {
      this.toastr.warning('A previsão de término deve ser posterior à data inicial.');
      return;
    }

    Swal.fire({
      title: 'Deseja realmente salvar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      confirmButtonColor: 'var(--primary-color)',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.tarefaService.adicionar(this.novoCompromisso).subscribe(response => {
          if (response.sucesso) {
            this.toastr.success('Tarefa salva com sucesso!');
            this.fecharModal();
            this.atualizarLista();
          } else {
            this.toastr.error('Erro ao salvar compromisso!');
          }
        });
      }
    });
  }

  private preencherModel() {
    const dataInicial = this.formNovoCompromisso.controls['dataInicial'].value;
    let dataPrevisaoFinalizacao = this.formNovoCompromisso.controls['dataPrevisaoFinalizacao'].value;

    if (!dataPrevisaoFinalizacao && dataInicial) {
      const previsao = new Date(dataInicial);
      previsao.setHours(previsao.getHours() + 1);
      dataPrevisaoFinalizacao = this.formatarDataParaInput(previsao);
      this.formNovoCompromisso.patchValue({ dataPrevisaoFinalizacao });
    }

    this.novoCompromisso.dataInicial = dataInicial;
    this.novoCompromisso.descricao = this.formNovoCompromisso.controls['descricao'].value;
    this.novoCompromisso.atendimentoId = null;
    this.novoCompromisso.dataPrevisaoFinalizacao = dataPrevisaoFinalizacao;
    this.novoCompromisso.descricaoDetalhada = this.formNovoCompromisso.controls['descricaoDetalhada'].value;
    this.novoCompromisso.tipoTarefaId = 1;
  }

  private construirFormulario(): void {
    this.formNovoCompromisso = this.formBuilder.group({
      descricao: [''],
      dataInicial: ['', Validators.required],
      dataPrevisaoFinalizacao: ['', Validators.required],
      descricaoDetalhada: ['']
    });
  }

  private atualizarLimitesDatas(): void {
    const agora = new Date();
    agora.setSeconds(0, 0);
    agora.setMinutes(agora.getMinutes() + 1);
    this.dataInicioMinima = this.formatarDataParaInput(agora);
    this.atualizarPrevisaoMinima(this.formNovoCompromisso.controls['dataInicial'].value);
  }

  private atualizarPrevisaoMinima(dataInicial: string): void {
    const inicio = dataInicial ? new Date(dataInicial) : new Date(this.dataInicioMinima);
    inicio.setMinutes(inicio.getMinutes() + 1);
    this.dataPrevisaoMinima = this.formatarDataParaInput(inicio);
  }
}
